/**
 * A configuração do robô pela USB, passo a passo. Sem React e sem porta de
 * verdade: recebe `Linhas` e a função que pede a credencial, então o teste
 * dirige o fluxo inteiro com uma porta falsa.
 *
 * DUAS regras que não são detalhe:
 *
 * 1. A credencial só é pedida DEPOIS que o robô respondeu. Emitir invalida a
 *    anterior na hora; emitir para um robô que nem está falando deixaria sem
 *    acesso o robô que funcionava.
 * 2. O token vive numa variável local, vai para a porta e sai de escopo. Nunca
 *    entra em evento, estado, log nem mensagem de erro.
 */
import {
  LINHA_INFO, LINHA_WIFI_LIST, LINHA_WIFI_SCAN, RESPOSTA_ETAPA, interpretarInfo, interpretarLinha,
  interpretarWifiDel, interpretarWifiList, interpretarWifiScan, linhaConfig, linhaEtapa,
  linhaWifiDel, type ConfigDoConsole, type InfoDoRobo, type NomeDaEtapa, type RedeSalva,
  type RedeVista, type RespostaWifiDel, type Wifi,
} from './protocolo'
import type { Linhas } from './serial'

export type Passo =
  | 'boot' | 'identificar' | 'credencial' | 'gravar' | 'wifi' | 'hora' | 'console'

export type Evento =
  | { tipo: 'passo'; passo: Passo }
  | { tipo: 'identificado'; info: InfoDoRobo | null }
  | { tipo: 'concluido'; passo: Passo }

export type Resultado =
  | { ok: true; info: InfoDoRobo | null }
  | { ok: false; passo: Passo; motivo: string }

export type Prazos = {
  boot: number
  aposBoot: number
  resposta: number
  acompanhar: number
}

/**
 * Medidos no Robô I em 23/09/2026: a ponte CP2102 reinicia o ESP32 ao abrir a
 * porta, e o boot leva ~4,3 s até a primeira linha `LIVE:`. Placa sem essa
 * ponte não imprime boot nenhum; então, vencido o prazo, segue assim mesmo.
 * O acompanhamento cobre as tentativas de Wi-Fi de 30 s do firmware.
 *
 * `resposta` e' o teto do laço do firmware preso numa consulta HTTPS, não o
 * tempo típico: ele só lê a USB entre uma consulta e outra, e a do snapshot
 * pode segurar ~18 s (handshake 8 + conexão 5 + leitura 5, DirectConsole.h).
 * Medido no Robô II em 24/09/2026: 8,1 s numa de 58 perguntas. Com 4 s a tela
 * desistia e fechava a porta — o que reinicia a placa e descarta o CONFIG que
 * esperava no buffer. Firmware antigo continua detectado na hora: responde
 * CONFIG_INVALIDA ao INFO sem esperar consulta nenhuma.
 */
export const PRAZOS_PADRAO: Prazos = { boot: 12_000, aposBoot: 1_500, resposta: 25_000, acompanhar: 75_000 }

type DependenciasDaPorta = {
  linhas: Linhas
  aoAvancar: (e: Evento) => void
  agora?: () => number
  esperar?: (ms: number) => Promise<void>
  prazos?: Prazos
}

type Dependencias = DependenciasDaPorta & {
  pedirCredencial: () => Promise<ConfigDoConsole>
}

const esperarReal = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Lê linhas até `aceita` devolver algo, ou até o prazo. */
export async function ateQue<T>(
  linhas: Pick<Linhas, 'proxima'>, prazoMs: number, agora: () => number,
  aceita: (linha: string) => T | undefined,
): Promise<T | undefined> {
  const fim = agora() + prazoMs
  for (;;) {
    const resta = fim - agora()
    if (resta <= 0) return undefined
    const linha = await linhas.proxima(resta)
    if (linha === null) return undefined
    const r = aceita(linha)
    if (r !== undefined) return r
  }
}

/**
 * As etapas encenadas (spec 2026-09-24-robo-painel-de-ajustes, 4.5). A resposta ao
 * `ETAPA` não é de ninguém: ela chega ANTES da resposta da linha seguinte (o firmware
 * responde na ordem), e quem lê a porta não pode tomá-la por essa resposta — um
 * `CONFIG_INVALIDA` do firmware antigo ao `ETAPA` passaria por "firmware sem INFO".
 *
 * O estado é da PORTA, não de um passo: identificar e gravar são chamadas separadas
 * sobre as mesmas linhas, e a segunda precisa saber o que a primeira descobriu.
 * - `suporta`: `null` até a primeira resposta; `false` depois de um `CONFIG_INVALIDA`
 *   a um `ETAPA` (aí nenhum outro é mandado); `true` depois de um `ETAPA_OK`.
 * - `pendentes`: quantos `ETAPA` ainda não foram respondidos.
 */
type EstadoDasEtapas = { suporta: boolean | null; pendentes: number }
const etapasDaPorta = new WeakMap<Linhas, EstadoDasEtapas>()

export type LinhasComEtapas = Linhas & { anunciar: (nome: NomeDaEtapa) => Promise<void> }

export function comEtapas(base: Linhas): LinhasComEtapas {
  let estado = etapasDaPorta.get(base)
  if (!estado) { estado = { suporta: null, pendentes: 0 }; etapasDaPorta.set(base, estado) }
  const e = estado
  return {
    async proxima(prazoMs) {
      for (;;) {
        const l = await base.proxima(prazoMs)
        if (l === null) return null
        if (l === RESPOSTA_ETAPA) {
          e.suporta = true
          e.pendentes = Math.max(0, e.pendentes - 1)
          continue
        }
        // Só firmware que ainda não provou saber `ETAPA` pode estar recusando um.
        if (l === 'CONFIG_INVALIDA' && e.pendentes > 0 && e.suporta !== true) {
          e.suporta = false
          e.pendentes -= 1
          continue
        }
        return l
      }
    },
    escrever: (l) => base.escrever(l),
    fechar: () => base.fechar(),
    async anunciar(nome) {
      if (e.suporta === false) return
      e.pendentes += 1
      await base.escrever(linhaEtapa(nome))
    },
  }
}

/** `ETAPA fim` sem deixar o erro da porta (já fechada, cabo fora) mascarar o resultado. */
async function encerrarEtapas(linhas: LinhasComEtapas): Promise<void> {
  try { await linhas.anunciar('fim') } catch { /* a cena expira sozinha em 60 s */ }
}

/**
 * Espera o boot e pergunta `INFO`. `null` = firmware antigo (responde
 * CONFIG_INVALIDA); `undefined` = o robô não respondeu. Serve à configuração
 * e à gravação — nas duas, nada acontece antes de o robô ter falado.
 */
export async function identificarRobo(
  linhas: Linhas, prazos: Prazos, agora: () => number,
  esperar: (ms: number) => Promise<void>,
  aoFimDoBoot: () => void = () => {},
  antesDoInfo: () => Promise<void> = async () => {},
): Promise<{ info: InfoDoRobo | null } | undefined> {
  // Deixar o robô terminar de ligar — a linha mandada durante o boot some.
  const bootou = await ateQue(linhas, prazos.boot, agora,
    (l) => (l.startsWith('LIVE:') ? true : undefined))
  if (bootou) await esperar(prazos.aposBoot)
  aoFimDoBoot()
  await antesDoInfo()
  await linhas.escrever(LINHA_INFO)
  return ateQue(linhas, prazos.resposta, agora, (l) => {
    if (l.startsWith('INFO ')) return { info: interpretarInfo(l) }
    if (l === 'CONFIG_INVALIDA') return { info: null }
    return undefined
  })
}

/**
 * As redes que o robô conhece e as que enxerga agora. `vistas` nulo: o
 * firmware tem `WIFI_LIST` e não respondeu ao `WIFI_SCAN` — a tela fica com o
 * nome digitado.
 */
export type RedesDoRobo = { salvas: RedeSalva[]; vistas: RedeVista[] | null }

export type Identificacao =
  | { ok: true; info: InfoDoRobo | null; redes: RedesDoRobo | null }
  | { ok: false; passo: Passo; motivo: string }

/** `WIFI_LIST`: a lista, `null` para CONFIG_INVALIDA ou linha malformada, `undefined` sem resposta. */
export async function listarRedesSalvas(
  linhas: Linhas, prazos: Prazos = PRAZOS_PADRAO, agora: () => number = Date.now,
): Promise<RedeSalva[] | null | undefined> {
  await linhas.escrever(LINHA_WIFI_LIST)
  return ateQue(linhas, prazos.resposta, agora, (l) => {
    if (l.startsWith(LINHA_WIFI_LIST + ' ')) return interpretarWifiList(l)
    if (l === 'CONFIG_INVALIDA') return null
    return undefined
  })
}

/**
 * `WIFI_LIST` e depois `WIFI_SCAN`, os dois no prazo `resposta`: o scan
 * bloqueia o laço do firmware ~2-3 s, e o laço pode já estar preso numa
 * consulta HTTPS de até 18 s. `null` = firmware sem os comandos.
 */
export async function perguntarRedes(
  linhas: Linhas, prazos: Prazos = PRAZOS_PADRAO, agora: () => number = Date.now,
): Promise<RedesDoRobo | null> {
  const salvas = await listarRedesSalvas(linhas, prazos, agora)
  if (!salvas) return null
  await linhas.escrever(LINHA_WIFI_SCAN)
  const vistas = await ateQue(linhas, prazos.resposta, agora, (l) => {
    if (l.startsWith(LINHA_WIFI_SCAN + ' ')) return interpretarWifiScan(l)
    if (l === 'CONFIG_INVALIDA') return null
    return undefined
  })
  return { salvas, vistas: vistas ?? null }
}

/**
 * `WIFI_DEL`. O firmware recusa a última rede confirmada com `WIFI_ULTIMA`
 * (spec 3.4), além do console. `undefined` = não respondeu.
 */
export async function esquecerRede(
  linhas: Linhas, ssid: string, prazos: Prazos = PRAZOS_PADRAO, agora: () => number = Date.now,
): Promise<RespostaWifiDel | undefined> {
  await linhas.escrever(linhaWifiDel(ssid))
  return ateQue(linhas, prazos.resposta, agora, interpretarWifiDel)
}

/**
 * Passos 1 e 2 — boot e "quem é você?" — e, com firmware que os tem, as redes.
 * Não emite credencial: a pessoa escolhe a rede depois disto, com a porta
 * ainda aberta (fechar reinicia a placa CP2102).
 */
export async function identificarPelaUsb(
  deps: DependenciasDaPorta, { perguntarWifi = true }: { perguntarWifi?: boolean } = {},
): Promise<Identificacao> {
  const { aoAvancar } = deps
  // A porta com as etapas: o robô acena enquanto é identificado (`ETAPA identificar`).
  const linhas = comEtapas(deps.linhas)
  const agora = deps.agora ?? Date.now
  const esperar = deps.esperar ?? esperarReal
  const prazos = deps.prazos ?? PRAZOS_PADRAO

  // Firmware antigo responde CONFIG_INVALIDA ao INFO: segue, sem info.
  aoAvancar({ tipo: 'passo', passo: 'boot' })
  const resposta = await identificarRobo(linhas, prazos, agora, esperar, () => {
    aoAvancar({ tipo: 'concluido', passo: 'boot' })
    aoAvancar({ tipo: 'passo', passo: 'identificar' })
  }, () => linhas.anunciar('identificar'))
  if (resposta === undefined) {
    await encerrarEtapas(linhas)
    return {
      ok: false, passo: 'identificar',
      motivo: 'O robô não respondeu pela USB. Confira o cabo e se a porta escolhida é a dele, e tente de novo.',
    }
  }
  aoAvancar({ tipo: 'identificado', info: resposta.info })
  // Firmware sem INFO é anterior aos comandos de Wi-Fi: nem pergunta.
  const redes = perguntarWifi && resposta.info ? await perguntarRedes(linhas, prazos, agora) : null
  aoAvancar({ tipo: 'concluido', passo: 'identificar' })
  return { ok: true, info: resposta.info, redes }
}

/**
 * Passos 3 em diante, com o robô já identificado e a porta aberta: emite a
 * credencial, grava e acompanha até o console aceitar.
 */
export async function gravarPelaUsb(
  deps: Dependencias, wifi: Wifi, info: InfoDoRobo | null,
): Promise<Resultado> {
  // `ETAPA fim` no fim E em toda saída antecipada: o robô não fica esperando a chave
  // de um fluxo que já acabou (a cena também expira sozinha, em 60 s).
  const linhas = comEtapas(deps.linhas)
  try {
    return await gravarComEtapas({ ...deps, linhas }, wifi, info)
  } finally {
    await encerrarEtapas(linhas)
  }
}

async function gravarComEtapas(
  deps: Dependencias & { linhas: LinhasComEtapas }, wifi: Wifi, info: InfoDoRobo | null,
): Promise<Resultado> {
  const { linhas, aoAvancar } = deps
  const agora = deps.agora ?? Date.now
  const prazos = deps.prazos ?? PRAZOS_PADRAO

  // 3. Só agora a credencial — ver a regra 1 no topo. O robô olha para cima e estende a
  // mão enquanto o console emite (`ETAPA credencial`): é a única etapa que só a tela sabe.
  aoAvancar({ tipo: 'passo', passo: 'credencial' })
  await linhas.anunciar('credencial')
  let linha: string
  // A segunda linha (revisão R2): só existe quando o console mandou `legado`.
  // Mesmo token da primeira — pedir outra credencial invalidaria a que acabou
  // de ir para o robô.
  let linhaLegado = ''
  try {
    const cfg = await deps.pedirCredencial()
    // A rede padrão vem com a credencial, quando foi pedida (spec 2026-09-29-
    // robo-wifi-padrao, 4.5). A senha dela segue a regra 2 do token: variável
    // local, vai para a porta e sai de escopo.
    const efetivo: Wifi = cfg.wifi_padrao
      ? { ssid: cfg.wifi_padrao.ssid, password: cfg.wifi_padrao.senha }
      : wifi
    linha = linhaConfig(cfg, efetivo)
    if (cfg.legado) {
      linhaLegado = linhaConfig({
        token: cfg.token, device_id: cfg.device_id,
        tenant_id: cfg.legado.tenant_id, snapshot_path: cfg.legado.snapshot_path,
      }, efetivo)
    }
  } catch {
    return {
      ok: false, passo: 'credencial',
      motivo: 'O console não emitiu a credencial. A credencial anterior do robô continua valendo.',
    }
  }
  aoAvancar({ tipo: 'concluido', passo: 'credencial' })

  // 4. Gravar. A partir daqui a credencial antiga já não vale.
  aoAvancar({ tipo: 'passo', passo: 'gravar' })
  await linhas.anunciar('gravar')
  const gravar = async (l: string) => {
    await linhas.escrever(l)
    return ateQue(linhas, prazos.resposta, agora, (r) => {
      if (r === 'CONFIG_OK') return true
      if (r === 'CONFIG_INVALIDA') return false
      return undefined
    })
  }
  let gravou = await gravar(linha)
  linha = ''
  // Só a recusa EXPLÍCITA libera a segunda linha: o firmware anterior à parte D
  // recusa o CONFIG sem tenant sem gravar nada. Prazo vencido (`undefined`)
  // não: não se sabe se a primeira gravou, e a segunda a sobrescreveria.
  if (gravou === false && linhaLegado) {
    gravou = await gravar(linhaLegado)
  }
  linhaLegado = ''
  if (gravou !== true) {
    return {
      ok: false, passo: 'gravar',
      motivo: gravou === false
        ? 'O robô recusou a configuração. A credencial anterior já foi invalidada: tente de novo, que uma nova é emitida.'
        : 'O robô não confirmou a gravação. A credencial anterior já foi invalidada: tente de novo, que uma nova é emitida.',
    }
  }
  aoAvancar({ tipo: 'concluido', passo: 'gravar' })

  // 5. Acompanhar até o console aceitar. `HTTPS GET status=` sozinho já prova
  // Wi-Fi e hora: o firmware só faz o GET com os dois.
  aoAvancar({ tipo: 'passo', passo: 'wifi' })
  let viuWifi = false
  let viuHora = false
  const status = await ateQue(linhas, prazos.acompanhar, agora, (l) => {
    const s = interpretarLinha(l)
    if (s.tipo === 'wifi' && !viuWifi) {
      viuWifi = true
      aoAvancar({ tipo: 'concluido', passo: 'wifi' })
      aoAvancar({ tipo: 'passo', passo: 'hora' })
    } else if (s.tipo === 'hora' && !viuHora) {
      viuHora = true
      aoAvancar({ tipo: 'concluido', passo: 'hora' })
      aoAvancar({ tipo: 'passo', passo: 'console' })
    } else if (s.tipo === 'console') {
      return s.status
    }
    return undefined
  })

  if (status === undefined) {
    if (!viuWifi) {
      return {
        ok: false, passo: 'wifi',
        motivo: 'O robô não conectou no Wi-Fi. Confira o nome da rede e a senha, e se a rede é de 2,4 GHz.',
      }
    }
    if (!viuHora) {
      return {
        ok: false, passo: 'hora',
        motivo: 'Conectou no Wi-Fi mas não acertou o relógio. A rede pode estar bloqueando NTP (porta 123).',
      }
    }
    return {
      ok: false, passo: 'console',
      motivo: 'Acertou o relógio mas não falou com o console. A rede pode estar bloqueando HTTPS de saída.',
    }
  }
  if (!viuWifi) aoAvancar({ tipo: 'concluido', passo: 'wifi' })
  if (!viuHora) aoAvancar({ tipo: 'concluido', passo: 'hora' })
  if (status === 401 || status === 403) {
    return {
      ok: false, passo: 'console',
      motivo: `O console recusou a credencial (HTTP ${status}). Tente de novo; se repetir, o robô pode estar suspenso.`,
    }
  }
  if (status !== 200) {
    return {
      ok: false, passo: 'console',
      motivo: `O robô chegou ao console, que respondeu HTTP ${status}. O robô tenta de novo sozinho a cada 30 s.`,
    }
  }
  aoAvancar({ tipo: 'concluido', passo: 'console' })
  return { ok: true, info }
}

/** O caminho de uma vez só, com o Wi-Fi já digitado: identificar e gravar. */
export async function configurarPelaUsb(deps: Dependencias, wifi: Wifi): Promise<Resultado> {
  const id = await identificarPelaUsb(deps, { perguntarWifi: false })
  if (!id.ok) return id
  return gravarPelaUsb(deps, wifi, id.info)
}

/**
 * Joga fora as linhas que chegaram sem ninguém pedir, antes de uma pergunta.
 * Sem isso, a resposta atrasada de uma pergunta anterior (um `AJUSTE_OK` que
 * chegou depois do prazo) seria lida como resposta à próxima.
 */
export async function descartarPendentes(linhas: Pick<Linhas, 'proxima'>, limite = 1000): Promise<void> {
  for (let i = 0; i < limite; i++) {
    if ((await linhas.proxima(0)) === null) return
  }
}

/** Escreve uma linha e espera a resposta que `aceita` reconhece, no prazo. */
export async function perguntar<T>(
  linhas: Pick<Linhas, 'proxima' | 'escrever'>, linha: string, prazoMs: number,
  aceita: (linha: string) => T | undefined, agora: () => number = Date.now,
): Promise<T | undefined> {
  await descartarPendentes(linhas)
  await linhas.escrever(linha)
  return ateQue(linhas, prazoMs, agora, aceita)
}
